import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Table, Space, message, Tag, Spin } from 'antd';
import { Edit, Trash2, PlusCircle } from 'lucide-react';
import { 
  RESOURCE_LEVEL_PERMISSIONS, 
  RESOURCE_TYPES,
  PERMISSION_DESCRIPTIONS 
} from '../../types/permissions';
import { fetchUsers, updateUserPermissions } from '../../actions/users';
import PermissionModal from '../../components/Modals/Users/PermissionModal';

const UserPermissions = () => {
  const { userId } = useParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [resources, setResources] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const users = useSelector(state => state?.users?.users);
  // const user = useSelector(state => state?.auth?.user);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (users && userId) {
      const user = users.find(u => u.userId === userId);
      setCurrentUser(user);
    }
  }, [users, userId]);

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsRes, resourcesRes] = await Promise.all([
        fetch('https://cloudify-prod.wizr.in/api/projects'),
        fetch('https://cloudify-prod.wizr.in/api/resources')
      ]);
      setProjects(await projectsRes.json());
      setResources(await resourcesRes.json());
    } catch (error) {
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleModalSubmit = async (permissionData) => {
    try {
      await dispatch(updateUserPermissions(userId, permissionData));

      message.success('Permissions updated successfully');
      setModalVisible(false);
      setEditingPermission(null);
      loadData();
    } catch (error) {
      message.error('Failed to update permissions');
    }
  };

  const handlePermissionDelete = async (resourceId, projectId) => {
    try {
      await dispatch(updateUserPermissions(userId, {
        projectId,
        resourceId,
        permissions: null
      }));
      message.success('Permission removed successfully');
      loadData();
    } catch (error) {
      message.error('Failed to remove permission');
    }
  };

  const getPermissionData = () => {
    if (!currentUser?.projectPermissions || !projects.length) return [];
    
    return Object.entries(currentUser.projectPermissions).flatMap(([projectId, projectPerms]) => {
      const project = projects.find(p => p.id === projectId);
      if (!project) return [];
      
      return Object.entries(projectPerms.resources || {}).map(([resourceId, perm]) => {
        const resource = resources.find(r => r.id === resourceId);
        return resource ? {
          key: `resource-${resourceId}`,
          id: resourceId,
          projectId,
          projectName: `${project.name}-${project.environment}`,
          resource: resource.name,
          resourceType: resource.type,
          permissions: perm
        } : null;
      }).filter(Boolean);
    });
  };

  const renderPermissionTags = (permissions) => {
    if (!permissions) return <Tag>No Access</Tag>;

    if (permissions === RESOURCE_LEVEL_PERMISSIONS.ADMIN) {
      return <Tag color="gold">Admin</Tag>;
    }

    if (permissions === RESOURCE_LEVEL_PERMISSIONS.READ) {
      return <Tag color="blue">Read</Tag>;
    }

    if (Array.isArray(permissions)) {
      return (
        <Space size={[0, 4]} wrap>
          {permissions.map(perm => (
            <Tag key={perm} color="green">{perm}</Tag>
          ))}
        </Space>
      );
    }

    return <Tag color="gray">Unknown</Tag>;
  };

  const columns = [
    {
      title: 'Project',
      dataIndex: 'projectName',
      key: 'projectName',
      render: text => <span className="text-gray-700 font-medium">{text}</span>,
      width: '20%'
    },
    {
      title: 'Resource',
      dataIndex: 'resource',
      key: 'resource',
      width: '40%',
      render: (text, record) => (
        <div className="flex items-center">
          <span className="text-gray-700">{text}</span>
          <Tag color="geekblue" className="ml-2">
            {RESOURCE_TYPES[record.resourceType]}
          </Tag>
        </div>
      )
    },
    {
      title: 'Permissions',
      key: 'permissions',
      width: '30%',
      render: (_, record) => (
        <div className="flex flex-wrap gap-1">
          {renderPermissionTags(record.permissions)}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Space>
          <button
            onClick={() => {
              setEditingPermission({
                projectId: record.projectId,
                resourceId: record.id,
                resourceType: record.resourceType,
                permissions: record.permissions
              });
              setModalVisible(true);
            }}
            disabled={!editMode}
            className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-full hover:bg-indigo-50 transition-colors duration-200"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handlePermissionDelete(record.id, record.projectId)}
            disabled={!editMode}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors duration-200"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </Space>
      ),
    },
  ];

  if (loading) return <div className="flex justify-center p-8"><Spin /></div>;
  if (!currentUser) return <div>User not found</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">User Permissions</h2>
          <p className="text-gray-500 text-sm">Manage permissions for {currentUser.name || currentUser.email}</p>
        </div>
        <button
          onClick={() => editMode ? setModalVisible(true) : setEditMode(true)}
          className="inline-flex items-center text-sm px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 text-white rounded-lg transition-all duration-200 font-medium shadow-md gap-2"
        >
          {editMode && <PlusCircle className="h-5 w-5"/>}
          {editMode ? 'Add Permission' : 'Manage Permissions'}
        </button>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <Table
          columns={columns}
          dataSource={getPermissionData()}
          rowKey="key"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} permissions`,
          }}
        />
      </div>
      
      {editMode && (
        <div className="flex justify-end">
          <button
            onClick={() => setEditMode(false)}
            className="text-sm px-4 py-2 text-gray-600 hover:text-gray-800 font-medium rounded-lg transition-all duration-200"
          >
            Cancel
          </button>
        </div>
      )}

      <PermissionModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleModalSubmit}
        projects={projects}
        resources={resources}
        initialValues={editingPermission}
      />
    </div>
  );
};

export default UserPermissions;